import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SearchInput } from '@/components/ui/search-input';
import type { TaskType, TaskStatus, TaskLifecycleState } from '@pms/shared';
import { tasksService } from '@/services/tasks.service';
import { departmentsService } from '@/services/departments.service';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { TaskRow } from '@/components/tasks/TaskRow';
import { TaskDetailDrawer } from '@/components/tasks/TaskDetailDrawer';
import { ALL_TYPES, ALL_STATUSES, TYPE_META, STATUS_META } from '@/components/tasks/taskConstants';

interface ByTasksTabProps {
  /** 'stopped' restricts to stopped tasks (Stopped Tasks view). */
  lifecycleState?: TaskLifecycleState;
}

export function ByTasksTab({ lifecycleState }: ByTasksTabProps) {
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('all');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState<string | null>(null);

  const deptsQ = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tasks', 'list', { search, dept, type, status, page, lifecycleState }],
    queryFn: () => tasksService.list({
      q: search.trim() || undefined,
      department_id: dept === 'all' ? undefined : dept,
      task_type: type === 'all' ? undefined : (type as TaskType),
      status: status === 'all' ? undefined : (status as TaskStatus),
      lifecycle_state: lifecycleState,
      page,
      limit: 20,
    }),
  });

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  const total = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4 lg:flex-row">
        <SearchInput
          value={search}
          onChange={resetPage(setSearch)}
          placeholder="Search tasks or assignees…"
          containerClassName="flex-1"
        />
        <Select value={dept} onValueChange={resetPage(setDept)}>
          <SelectTrigger className="lg:w-48"><SelectValue placeholder="All Departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {(deptsQ.data?.data ?? []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={resetPage(setType)}>
          <SelectTrigger className="lg:w-40"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {ALL_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={resetPage(setStatus)}>
          <SelectTrigger className="lg:w-44"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <p className="text-sm text-muted-foreground">{total} task{total === 1 ? '' : 's'}</p>

      <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
        {isLoading && <div className="space-y-px p-1">{[...Array(8)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded bg-muted/40" />)}</div>}
        {isError && <div className="p-10 text-center text-sm text-muted-foreground">Couldn’t load tasks. <button className="text-primary underline" onClick={() => refetch()}>Retry</button></div>}
        {data && data.data.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">No tasks match these filters.</div>
        )}
        {data?.data.map((t) => <TaskRow key={t.id} task={t} onClick={() => setDetailId(t.id)} />)}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {totalPages} · {total} total</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <TaskDetailDrawer taskId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}
